import { CSSProperties, FC, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { openLink } from '@telegram-apps/sdk-react';

import { useProfileStore } from '@/store/useProfileStore.ts';
import { UserData } from '@/components/Registration/UserData.tsx';
import { Steps } from '@/components/common/Steps/Steps.tsx';
import { FormErrors } from '@/components/ProfileData/FormErrors.tsx';
import { getUserDataValidationSchema } from '@/validationSchemas/Registration/RegistrationPageSchemas.tsx';
import { routes } from '@/navigation/routes.tsx';
import { logError } from '@/utils/sentry-monitor.tsx';
import { helpers } from '@/helpers.ts';
import { MainButton } from '@/components/common/MainButton/MainButton.tsx';
import { useKeyboardLift } from '@/hooks/useKeyboardLift.ts';

export type UserDataFields = {
  name: string;
  about_me: string;
  gender: string;
  birthday: string;
  locale?: string;
};

const BAR_HEIGHT = 70;

const RegistrationUserData: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { aboutMe, updateProfile, updateSearchFilters } = useProfileStore();

  const [loading, setLoading] = useState(false);

  const { offset, stableHeight } = useKeyboardLift({
    barHeight: BAR_HEIGHT
  });

  const [data, setData] = useState<UserDataFields>({
    name: aboutMe?.profile.name ?? '',
    about_me: aboutMe?.profile.about_me ?? '',
    gender: aboutMe?.profile.gender ?? '',
    birthday: aboutMe?.profile.birthday ?? '',
    locale: aboutMe?.profile.locale ?? ''
  });

  function updateFields(fields: Partial<UserDataFields>) {
    setData((prev) => ({ ...prev, ...fields }));
  }

  const onSubmit = async () => {
    try {
      setLoading(true);
      const payload = {
        name: data.name,
        birthday: data.birthday,
        gender: data.gender,
        about_me: data.about_me
      };

      const searchFilters = {
        search_gender: payload.gender === 'f' ? 'm' : 'f',
        ...helpers.calculateAgeRange(helpers.calculateAge(data.birthday))
      };

      await updateProfile(payload);
      await updateSearchFilters(searchFilters);

      navigate(routes.registrationUserPhotos);
    } catch (e) {
      logError(e, { context: 'First step login error' }, true);
    } finally {
      setLoading(false);
    }
  };

  const validationSchema = getUserDataValidationSchema(data);

  const methods = useForm({
    resolver: validationSchema ? zodResolver(validationSchema) : undefined,
    defaultValues: data
  });

  return (
    <FormProvider {...methods}>
      <form style={formStyle}>
        <Steps count={3} progress={1} />
        <UserData {...data} updateFields={updateFields} />
      </form>
      <FormErrors />

      {createPortal(
        <div style={{ ...portalShellStyle, height: stableHeight }}>
          <div
            style={{
              ...buttonBarStyle,
              transform: `translateY(-${offset}px)`
            }}
          >
            <MainButton
              onClick={methods.handleSubmit(onSubmit)}
              text={t('button.continue')}
              loading={loading}
              disabled={loading}
              fixed={false}
            />
            <div style={consentStyle}>
              <Trans
                i18nKey="registration.consent"
                components={{
                  br: <br />,
                  terms: (
                    <span
                      onClick={() => {
                        openLink('https://kyivdate.app/terms/');
                      }}
                      style={linkStyle}
                    />
                  ),
                  privacy: (
                    <span
                      onClick={() => {
                        openLink('https://kyivdate.app/privacy/');
                      }}
                      style={linkStyle}
                    />
                  )
                }}
              />
            </div>
          </div>
        </div>,
        document.body
      )}
    </FormProvider>
  );
};

const formStyle: CSSProperties = {
  paddingTop: '100px',
  paddingBottom: BAR_HEIGHT + 50
};

const portalShellStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  pointerEvents: 'none',
  overflow: 'hidden'
};

const buttonBarStyle: CSSProperties = {
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  padding: '5px 18px 15px 18px',
  background:
    'linear-gradient(to top, #f5f5f5 60%, rgba(245, 245, 245, 0.85) 80%, rgba(245, 245, 245, 0))',
  zIndex: 10,
  pointerEvents: 'auto'
};

const consentStyle: CSSProperties = {
  fontSize: '11px',
  fontWeight: 510,
  paddingBottom: 10,
  color: '#787878',
  textAlign: 'center',
  paddingLeft: '16px',
  paddingRight: '16px'
};

const linkStyle: CSSProperties = {
  color: '#787878',
  textDecoration: 'underline'
};

export default RegistrationUserData;
